import jsPDF from 'jspdf';
import 'jspdf-autotable';

export const generatePDF = (formData, results) => {
  const doc = new jsPDF();
  
  // Set up colors
  const primaryBlue = [30, 64, 175];
  const lightBlue = [59, 130, 246];
  const gray = [75, 85, 99];
  
  // Header with logo section
  doc.setFillColor(primaryBlue[0], primaryBlue[1], primaryBlue[2]);
  doc.rect(0, 0, 210, 40, 'F');
  
  // Company name
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('ALGARVE SEASONS', 105, 15, { align: 'center' });
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text('Air Conditioning & Renewable Energies', 105, 23, { align: 'center' });
  doc.text('Professional BTU Calculation Report', 105, 30, { align: 'center' });
  
  // Date
  doc.setFontSize(10);
  const today = new Date().toLocaleDateString('en-GB');
  doc.text(`Report Date: ${today}`, 105, 36, { align: 'center' });
  
  // Main Results Section
  let yPos = 50;
  doc.setFillColor(lightBlue[0], lightBlue[1], lightBlue[2]);
  doc.rect(15, yPos, 180, 35, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('CALCULATION RESULTS', 105, yPos + 10, { align: 'center' });
  
  doc.setFontSize(32);
  doc.text(results.totalBTU.toLocaleString(), 70, yPos + 25, { align: 'center' });
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text('BTU/hour', 70, yPos + 31, { align: 'center' });
  
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text(results.totalKW.toFixed(2), 140, yPos + 25, { align: 'center' });
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text('kW', 140, yPos + 31, { align: 'center' });
  
  // Room Details
  yPos = 95;
  doc.setTextColor(gray[0], gray[1], gray[2]);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Room Specifications', 15, yPos);
  
  yPos += 8;
  const roomDetails = [
    ['Dimensions', `${formData.roomLength}m × ${formData.roomWidth}m × ${formData.roomHeight}m`],
    ['Room Area', `${results.roomArea.toFixed(1)} m²`],
    ['Room Volume', `${results.roomVolume.toFixed(1)} m³`],
    ['Room Type', formData.roomType.replace('-', ' ').toUpperCase()]
  ];
  
  doc.autoTable({
    startY: yPos,
    head: [['Property', 'Value']],
    body: roomDetails,
    theme: 'striped',
    headStyles: { fillColor: primaryBlue },
    margin: { left: 15, right: 15 }
  });
  
  // Building Characteristics
  yPos = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Building Characteristics', 15, yPos);
  
  yPos += 8;
  const buildingDetails = [
    ['Insulation Quality', formData.insulationQuality.toUpperCase()],
    ['External Walls', `${formData.externalWalls} wall(s)`],
    ['Building Age', formData.buildingAge],
    ['Floor Location', formData.floorLocation.toUpperCase()]
  ];
  
  doc.autoTable({
    startY: yPos,
    head: [['Property', 'Value']],
    body: buildingDetails,
    theme: 'striped',
    headStyles: { fillColor: primaryBlue },
    margin: { left: 15, right: 15 }
  });
  
  // Environmental Factors
  yPos = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Environmental Factors', 15, yPos);
  
  yPos += 8;
  const envDetails = [
    ['Number of Windows', formData.numberOfWindows.toString()],
    ['Window Type', `${formData.windowType}-glazed`.toUpperCase()],
    ['Sun Exposure', formData.sunExposure.toUpperCase()],
    ['Window Orientation', formData.windowOrientation.toUpperCase()],
    ['Window Shading', formData.windowShading.toUpperCase()]
  ];
  
  doc.autoTable({
    startY: yPos,
    head: [['Property', 'Value']],
    body: envDetails,
    theme: 'striped',
    headStyles: { fillColor: primaryBlue },
    margin: { left: 15, right: 15 }
  });
  
  // Add new page for breakdown
  doc.addPage();
  
  // Calculation Breakdown
  yPos = 20;
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(primaryBlue[0], primaryBlue[1], primaryBlue[2]);
  doc.text('Detailed Calculation Breakdown', 15, yPos);
  
  yPos += 10;
  const breakdownData = Object.entries(results.breakdown).map(([key, value]) => {
    const labels = {
      base: 'Base Room Load',
      insulation: 'Insulation Factor',
      sun: 'Sun Exposure',
      occupants: 'Occupants',
      appliances: 'Appliances',
      other: 'Other Factors'
    };
    const percentage = ((value / results.totalBTU) * 100).toFixed(1);
    return [labels[key], `${value.toLocaleString()} BTU`, `${percentage}%`];
  });
  
  doc.autoTable({
    startY: yPos,
    head: [['Component', 'BTU Value', 'Percentage']],
    body: breakdownData,
    theme: 'striped',
    headStyles: { fillColor: primaryBlue },
    margin: { left: 15, right: 15 },
    foot: [['TOTAL', `${results.totalBTU.toLocaleString()} BTU`, '100%']],
    footStyles: { fillColor: primaryBlue, fontStyle: 'bold' }
  });
  
  // Occupancy & Heat Sources
  yPos = doc.lastAutoTable.finalY + 10;
  doc.setTextColor(gray[0], gray[1], gray[2]);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Occupancy & Heat Sources', 15, yPos);
  
  yPos += 8;
  const occupancyDetails = [
    ['Number of Occupants', formData.numberOfOccupants.toString()],
    ['Activity Level', formData.activityLevel.toUpperCase()],
    ['Heat Appliances', formData.heatAppliances.toString()],
    ['Lighting Type', formData.lightingType.toUpperCase()],
    ['Kitchen in Space', formData.hasKitchen ? 'YES' : 'NO']
  ];
  
  doc.autoTable({
    startY: yPos,
    head: [['Property', 'Value']],
    body: occupancyDetails,
    theme: 'striped',
    headStyles: { fillColor: primaryBlue },
    margin: { left: 15, right: 15 }
  });
  
  // Climate Settings
  yPos = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Climate Settings', 15, yPos);
  
  yPos += 8;
  const climateDetails = [
    ['Outside Temperature', `${formData.outsideTemp}°C`],
    ['Desired Indoor Temp', `${formData.insideTemp}°C`],
    ['Temperature Difference', `${formData.outsideTemp - formData.insideTemp}°C`],
    ['Humidity Level', formData.humidityLevel.toUpperCase()]
  ];
  
  doc.autoTable({
    startY: yPos,
    head: [['Property', 'Value']],
    body: climateDetails,
    theme: 'striped',
    headStyles: { fillColor: primaryBlue },
    margin: { left: 15, right: 15 }
  });
  
  // Recommendations
  yPos = doc.lastAutoTable.finalY + 15;
  doc.setFillColor(240, 249, 255);
  doc.rect(15, yPos, 180, 35, 'F');
  
  doc.setTextColor(primaryBlue[0], primaryBlue[1], primaryBlue[2]);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Safety Margin Recommendation', 20, yPos + 8);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  const recommendText = 'We recommend adding 10-20% to the calculated capacity to account for';
  const recommendText2 = 'extreme conditions and ensure optimal performance throughout the year.';
  doc.text(recommendText, 20, yPos + 15);
  doc.text(recommendText2, 20, yPos + 20);
  
  doc.setFont('helvetica', 'bold');
  const withMargin = Math.round(results.totalBTU * 1.15);
  doc.text(`Recommended Capacity: ${withMargin.toLocaleString()} BTU`, 20, yPos + 28);
  
  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(9);
    doc.setTextColor(128, 128, 128);
    doc.text('Algarve Seasons - Professional HVAC Solutions', 105, 285, { align: 'center' });
    doc.text('Contact us for professional installation in the Algarve region', 105, 290, { align: 'center' });
    doc.text(`Page ${i} of ${pageCount}`, 195, 290, { align: 'right' });
  }
  
  // Save the PDF
  doc.save(`BTU-Calculation-${today.replace(/\//g, '-')}.pdf`);
};